/**
 * Which service an activation code may be redeemed against.
 *
 * Mirrors the backend's ServiceTypes. THREE states, and the absent one is a real
 * answer rather than a missing value:
 *
 *     null -> any service      1 -> GP only      2 -> mental health only
 *
 * Unscoped is the default: a code works everywhere unless somebody deliberately
 * narrows it, and every code issued before this field existed is null.
 */
export const SERVICE_TYPE = {
    gp: 1,
    mentalHealth: 2,
};

export const ANY_SERVICE = null;

/**
 * The dropdown, in order. The unrestricted option is FIRST and is the default,
 * so the least surprising choice is also the one a hurried admin lands on.
 *
 * `value` is "" for "any" because an HTML select cannot hold null - the form
 * converts it back on submit. Keeping that conversion in one place is the whole
 * reason this list is shared rather than written inline.
 */
export const SERVICE_TYPE_OPTIONS = [
    { value: "", label: "Any service" },
    { value: String(SERVICE_TYPE.gp), label: "GP only" },
    { value: String(SERVICE_TYPE.mentalHealth), label: "Mental Health only" },
];

/**
 * A select's string value as the API's number-or-null.
 *
 * "" and undefined both mean any service. Anything unrecognised also means any
 * service rather than a guess: sending a scope nobody chose would silently
 * narrow a code.
 */
export function toServiceType(value) {
    if (value === "" || value === null || value === undefined) {
        return ANY_SERVICE;
    }

    const asNumber = Number(value);

    return asNumber === SERVICE_TYPE.gp || asNumber === SERVICE_TYPE.mentalHealth
        ? asNumber
        : ANY_SERVICE;
}

/** The API's number-or-null as a select's string value. */
export function fromServiceType(serviceType) {
    return serviceType === null || serviceType === undefined ? "" : String(serviceType);
}

/**
 * How a code's scope reads in a list.
 *
 * "Any service" is said in WORDS, not left blank: a blank cell reads as missing
 * data, and an admin cannot tell it from a column this build does not populate.
 */
export function serviceTypeLabel(serviceType) {
    if (serviceType === SERVICE_TYPE.gp) {
        return "GP only";
    }

    if (serviceType === SERVICE_TYPE.mentalHealth) {
        return "Mental Health only";
    }

    return "Any service";
}
